import { BehaviorSubject } from "rxjs";
import { authService } from ".";
import { Chargeback } from "@/models/chargeback";
import { ChargebackFilters } from "@/filters/chargeback";
import { Dayjs } from "dayjs";
import { apiDateFormat, downloadFileFromApiResponse } from "@/utils";
import { ChargebackStatus } from "@/models/enum/chargeback-status";
import { ChargebackSuggestionsResponse } from "@/models/chargeback-suggestions";

export class ChargebackService {
  chargebacks = new BehaviorSubject<Chargeback[]>([]);
  filters = new BehaviorSubject<ChargebackFilters>(new ChargebackFilters());
  isFetching = new BehaviorSubject(false);
  pageLoaded = [0];
  page = 0;
  limit = 100;
  chargeback = new BehaviorSubject<Chargeback | null>(null);
  isFetchingOne = new BehaviorSubject(false);

  dispose = async () => {
    this.chargebacks.next([]);
    this.filters.next(new ChargebackFilters());
    this.page = 0;
    this.pageLoaded = [0];
  };

  disposeChargeback = async () => {
    this.chargeback.next(null);
  };

  getChargebacks = async () => {
    const chargebacks = await this._getChargebacks(0, this.limit);
    if (chargebacks) {
      this.pageLoaded = [0];
      this.chargebacks.next(chargebacks);
    }
  };

  refresh = async () => {
    const chargebacks = await this._getChargebacks(
      0,
      (this.page + 1) * this.limit,
      false
    );
    if (chargebacks) {
      this.chargebacks.next(chargebacks);
    }
  };

  exportChargebacks = async () => {
    try {
      const response = await authService.authFetch(
        `/chargebacks/export`,
        {
          method: "POST",
          body: JSON.stringify(this.filters.value),
        }
      );

      if (response?.ok) {
        await downloadFileFromApiResponse(response);
      }
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  next = async () => {
    if (!this.pageLoaded.includes(this.page + 1)) {
      this.pageLoaded.push(this.page + 1);
      const chargebacks = await this._getChargebacks(this.page + 1, this.limit);
      if (!chargebacks) {
        this.pageLoaded.pop();
      } else if (chargebacks.length > 0) {
        this.chargebacks.next([...this.chargebacks.value, ...chargebacks]);
      }
    }
  };

  _getChargebacks = async (
    page: number,
    limit: number,
    savePage: boolean = true
  ) => {
    try {
      this.isFetching.next(true);
      const response = await authService.authFetch(
        `/chargebacks?page=${page}&limit=${limit}`,
        {
          method: "POST",
          body: JSON.stringify(this.filters.value),
        }
      );
      if (response?.ok) {
        const chargebacks = (await response.json()) as Chargeback[];
        if (savePage && chargebacks.length > 0) {
          this.page = page;
        }
        return chargebacks;
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.isFetching.next(false);
    }
    return null;
  };

  setChargebackIds = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      chargebackIds: value.length === 0 ? undefined : value,
    });
  };

  setDateFrom = async (date: Dayjs | null) => {
    this.filters.next({
      ...this.filters.value,
      dateFrom: date?.format(apiDateFormat),
    });
  };

  setDateTo = async (date: Dayjs | null) => {
    this.filters.next({
      ...this.filters.value,
      dateTo: date?.format(apiDateFormat),
    });
  };

  setPayeeCodes = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      payeeCodes: value.length === 0 ? undefined : value,
    });
  };

  setAccountNames = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      accountNames: value.length === 0 ? undefined : value,
    });
  };

  setStatuses = async (value: ChargebackStatus[]) => {
    this.filters.next({
      ...this.filters.value,
      statuses: value.length === 0 ? undefined : value,
    });
  };

  setVendorCentralStatuses = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      vendorCentralStatuses: value.length === 0 ? undefined : value,
    });
  };

  setIssueTypes = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      issueTypes: value.length === 0 ? undefined : value,
    });
  };

  setEans = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      eans: value.length === 0 ? undefined : value,
    });
  };

  setAsins = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      asins: value.length === 0 ? undefined : value,
    });
  };

  setPurchaseOrders = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      purchaseOrders: value.length === 0 ? undefined : value,
    });
  };

  setInvoiceNumbers = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      invoiceNumbers: value.length === 0 ? undefined : value,
    });
  };

  setReversalInvoiceNumbers = async (value: string[]) => {
    this.filters.next({
      ...this.filters.value,
      reversalInvoiceNumbers: value.length === 0 ? undefined : value,
    });
  };

  getChargeback = async (id: string) => {
    try {
      this.isFetchingOne.next(true);
      const response = await authService.authFetch(`/chargebacks/${id}`, {
        method: "GET",
      });
      if (response?.ok) {
        this.chargeback.next(Object.assign(new Chargeback(), await response.json()));
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.isFetchingOne.next(false);
    }
  };

  getSuggestions = async (): Promise<ChargebackSuggestionsResponse | null> => {
    try {
      const response = await authService.authFetch(`/chargebacks/suggestions`, {
        method: "POST",
        body: JSON.stringify(this.filters.value),
      });
      if (response?.ok) {
        const data = (await response.json()) as ChargebackSuggestionsResponse;
        return data;
      }
    } catch (e) {
      console.log(e);
    }
    return null;
  };
}
